import type { QuoteRequestStatus } from '@repo/api-contracts';
import type { ReactElement, Ref } from 'react';
import { cn } from '@/shared/ui';
import { quoteStatusLabel } from '../model/status-labels';
import { QUOTE_STATUS_TONES, type QuoteStatusTone } from '../model/status-tone';

interface QuoteStatusBadgeProps {
  readonly status: QuoteRequestStatus;
  /** 주문된 견적의 주문 고객 화면 이름(목록·상세 응답의 orderDisplayStatus, 주문 설계 §8-4). 없으면 견적 상태 이름이다. */
  readonly orderDisplayStatus?: string | null;
  /** 상태가 바뀐 뒤 포커스를 옮길 수 있게 — 상세가 tabIndex -1 과 함께 넘긴다. */
  readonly ref?: Ref<HTMLSpanElement>;
  readonly tabIndex?: number;
  readonly className?: string;
}

/** 상품 상세의 "최소 n개" 칩과 같은 각진 작은 칩. 라임은 고객 응답이 필요한 상태에만 쓴다. */
const TONE_CLASS: Readonly<Record<QuoteStatusTone, string>> = {
  action: 'bg-brand-lime text-black',
  progress: 'border border-foreground/20 text-foreground',
  ordered: 'bg-foreground text-background',
  closed: 'bg-muted text-muted-foreground',
};

/** 견적 상태 배지 — 고객 화면 이름(quoteStatusLabel — 주문됨은 주문 이름)을 상태의 결(QUOTE_STATUS_TONES)에 맞춰 칠한다. */
export const QuoteStatusBadge = ({
  status,
  orderDisplayStatus = null,
  ref,
  tabIndex,
  className,
}: QuoteStatusBadgeProps): ReactElement => (
  <span
    ref={ref}
    tabIndex={tabIndex}
    className={cn(
      'inline-flex h-6 items-center px-2 text-xs font-semibold whitespace-nowrap',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      TONE_CLASS[QUOTE_STATUS_TONES[status]],
      className,
    )}
  >
    {quoteStatusLabel({ status, orderDisplayStatus })}
  </span>
);
